import React, { useEffect, useState } from 'react';
import './decisionConfirmation.scss';
import { Box, Skeleton, Typography } from '@mui/material';
import {
  KFButton,
  KFTextInput,
  withTranslation,
  WithTranslation,
} from '@kf/ic2fx';
import { colors } from 'styles/colors';
import TypographyWithI18N from 'shared/components/TypographyWithI18N';
import ActionWall from 'shared/components/ActionWall';
import { ICandidateProfileApplicationHistory } from 'modules/hiring/models/candidateProfile.model';
import { HMDecisionModalActions } from 'modules/hiring/models/hiringDecisionOption';
import DecisionConfirmationModalShimmer from './decisionConfirmationModalSkeleton';

export interface IDecisionModalConfirmation extends WithTranslation {
  id: string;
  data: ICandidateProfileApplicationHistory[];
  note?: string;
  setNote?: any;
  open: boolean;
  decision: string;
  handleClose: () => void;
  decisionLoader: boolean;
  handleMakeDecision: (activeDecision: HMDecisionModalActions) => Promise<void>;
}

const DecisionConfirmationModal = ({
  id,
  data,
  open,
  note,
  setNote,
  t: translate,
  decision,
  handleClose,
  handleMakeDecision,
  decisionLoader,
}: IDecisionModalConfirmation) => {
  const { firstName, lastName, jobTitle, requisitionID } = data[0] || {};

  useEffect(() => {
    setNote();
  }, []);
  return (
    <ActionWall
      dialogId={id}
      open={open}
      onCloseIcon={() => {
        handleClose();
      }}
      closeIconButton
      successDisabled={false}
      titleClassName='kfic2-panelTitleBold'
      headerContent={
        decisionLoader ? (
          <Skeleton
            data-testid='skeleton-1'
            variant='rectangular'
            width={224}
            height={34}
            animation='wave'
            sx={{
              bgcolor: (theme) => theme.palette.neutral?.main,
              mb: 2,
            }}
          />
        ) : (
          <Box
            sx={{
              marginTop: '12px',
              maxWidth: '53.25rem',
              wordBreak: 'break-word',
            }}
          >
            <Typography
              component={'span'}
              id='make-decision-candidate-result'
              data-testid='make-decision-candidate-result'
              className='kfic2-panelTitleBold'
              style={{
                whiteSpace: 'nowrap',
                width: 'auto',
                textAlign: 'left',
              }}
            >
              {translate(decision)}
            </Typography>
            <Typography
              component={'span'}
              id='make-decision-candidate-name'
              data-testid='make-decision-candidate-name'
              className='kfic2-panelTitleBold'
              style={{
                overflow: 'hidden',
                textAlign: 'left',
                marginLeft: '8px',
              }}
              title={`${firstName} ${lastName}`}
            >
              {`${firstName} ${lastName}`}
            </Typography>
          </Box>
        )
      }
      id={`${id}-modal`}
      tabIndex={0}
      aria-label={translate('candidate-profile-offer-decision-modal')}
      centerAlignment={true}
      dialogPaperSxProps={{
        p: 2.5,
        m: 0,
        maxWidth: '53.25rem',
      }}
      dialogContentSxProps={{
        mb: 0,
        pb: 0,
        maxHeight: 'calc(100% - 9.75rem)',
        '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
          backgroundColor: colors?.tertiary.white,
          width: '1rem',
        },
        '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
          borderRadius: '0.6rem',
          backgroundColor: colors?.background.darkGray,
          border: `0.25rem ${colors?.tertiary.white} solid `,
        },
      }}
      dialogTitleSxProps={{
        px: 0,
        mb: 0,
        zIndex: 1,
      }}
      closeButtonStyleProps={{
        top: 0,
        padding: 0,
      }}
      closeIconStyleProps={{
        width: '1.25rem',
        height: '1.25rem',
        marginTop: '-5px',
      }}
      sxTitle={{
        mt: 0,
      }}
      saveBtnSxProps={{
        mt: 2.5,
      }}
    >
      {decisionLoader ? (
        <DecisionConfirmationModalShimmer />
      ) : (
        <Box>
          <Box sx={{ py: '0.8rem' }}>
            <TypographyWithI18N
              className='kfic2-bodyText'
              data-testid='candidate-job'
              id='candidate-job'
              sx={{ fontWeight: 700 }}
              i18nKey={'job'}
            />
            <Typography
              id='make-decision-candidate-job'
              data-testid='make-decision-candidate-job'
              className='kfic2-bodyText'
              style={{
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                width: '98%',
                textAlign: 'left',
              }}
              title={`${jobTitle}(${requisitionID})`}
            >
              {`${jobTitle}(${requisitionID})`}
            </Typography>
          </Box>
          <Box sx={{ py: '0.8rem' }}>
            <TypographyWithI18N
              className='kfic2-bodyText'
              data-testid='confirmation-advance-info'
              id='confirmation-advance-info'
              sx={{ fontWeight: 700 }}
              i18nKey={'addNote'}
            />
            <TypographyWithI18N
              className='kfic2-bodyText'
              data-testid='confirmation-info'
              id='confirmation-info'
              i18nKey={'pleaseFeelFreeToProvide'}
            />
          </Box>
          <Box sx={{ py: '0rem' }}>
            <KFTextInput
              id='confirmation-box-text-input'
              data-testid='confirmation-box-text-input'
              maxRows={5}
              minRows={5}
              multiline
              placeholder={translate('addAnOptionalNoteHere')}
              rows={5}
              value={note}
              onChange={(e) => setNote(e?.target?.value)}
              inputProps={{
                maxLength: 1000,
              }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              whiteSpace: 'nowrap',
              mt: '1.25rem',
            }}
          >
            <KFButton
              data-testid='submit-modal-button'
              sx={{
                height: '2.5rem',
                width: '12.438rem',
                '&.Mui-disabled': {
                  color: `${colors.tertiary.darkGray} !important`,
                },
              }}
              disabled={!note?.trim()}
              onClick={() => {
                /* istanbul ignore else */
                if (decision === 'Offer') {
                  handleMakeDecision(HMDecisionModalActions.Offer);
                } else if (decision === 'Reject') {
                  handleMakeDecision(HMDecisionModalActions.Pass);
                }
              }}
            >
              {translate('submitHiringDecision')}
            </KFButton>
          </Box>
        </Box>
      )}
    </ActionWall>
  );
};

export default withTranslation()(DecisionConfirmationModal);
